import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Check, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { createLead } from '../lib/api';
import { trackGenerateLead, trackCTAClick } from '../lib/ga4';
import { useNavigate } from 'react-router-dom';

const QualifyingForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1 - Company
    company_name: '',
    sector: '',
    employee_count: '',
    website: '',
    // Step 2 - Contact
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    // Step 3 - Needs
    main_challenge: '',
    budget_range: '',
    timeline: '',
    additional_info: '',
    needs: [],
    consent: false,
  });

  // State for score data from localStorage
  const [scoreData, setScoreData] = useState({ score: null, level: null });

  // Load quiz data from localStorage (persistent) and sessionStorage
  useEffect(() => {
    const loadQuizData = () => {
      // Read from localStorage (primary source for score/level)
      const storedScore = localStorage.getItem('lead_score');
      const storedLevel = localStorage.getItem('lead_level');
      
      if (storedScore && storedLevel) {
        const scoreNum = parseInt(storedScore, 10);
        setScoreData(prev => {
          // Only update if different to avoid re-renders
          if (prev.score !== scoreNum || prev.level !== storedLevel) {
            console.log('[Form] Score loaded from localStorage:', scoreNum, storedLevel);
            return { score: scoreNum, level: storedLevel };
          }
          return prev;
        });
      }
      
      // Also read sessionStorage for sector pre-fill
      const storedQuizData = sessionStorage.getItem('quizData');
      if (storedQuizData) {
        const parsed = JSON.parse(storedQuizData);
        setQuizData(parsed);
        // Pre-fill sector if available and not already set
        if (parsed.sector) {
          setFormData(prev => {
            if (!prev.sector) {
              return { ...prev, sector: parsed.sector };
            }
            return prev;
          });
        }
      }
    };

    // Load initially
    loadQuizData();

    // Also listen for custom event from quiz
    const handleQuizComplete = () => {
      loadQuizData();
    };

    window.addEventListener('quizDataSet', handleQuizComplete);
    
    return () => {
      window.removeEventListener('quizDataSet', handleQuizComplete);
    };
  }, []);

  const steps = [
    { number: 1, label: 'Entreprise' },
    { number: 2, label: 'Contact' },
    { number: 3, label: 'Besoins' },
  ];

  const sectors = [
    { value: 'artisans', label: 'Artisans & BTP' },
    { value: 'restauration', label: 'Restauration & Hôtellerie' },
    { value: 'services', label: 'Entreprises de services' },
    { value: 'liberal', label: 'Professions libérales' },
    { value: 'commerce', label: 'Commerce & Retail' },
    { value: 'autre', label: 'Autre secteur' },
  ];

  const employeeCounts = [
    { value: '1', label: '1 personne (indépendant)' },
    { value: '2-5', label: '2 à 5 personnes' },
    { value: '6-10', label: '6 à 10 personnes' },
    { value: '11-50', label: '11 à 50 personnes' },
    { value: '50+', label: 'Plus de 50 personnes' },
  ];

  const needsOptions = [
    { value: 'ia_generative', label: 'IA générative' },
    { value: 'automatisation', label: 'Automatisation' },
    { value: 'management', label: 'Management & leadership' },
    { value: 'soft_skills', label: 'Soft skills' },
    { value: 'digital_web', label: 'Digital & Web' },
  ];

  const challenges = [
    { value: 'temps', label: 'Manque de temps pour les tâches à valeur ajoutée' },
    { value: 'couts', label: 'Besoin de réduire les coûts opérationnels' },
    { value: 'croissance', label: 'Difficultés à développer mon activité' },
    { value: 'qualite', label: 'Améliorer la qualité de service' },
    { value: 'concurrence', label: 'Rester compétitif face à la concurrence' },
  ];

  const budgetRanges = [
    { value: '<1000', label: 'Moins de 1 000€' },
    { value: '1000-5000', label: '1 000€ - 5 000€' },
    { value: '5000-10000', label: '5 000€ - 10 000€' },
    { value: '>10000', label: 'Plus de 10 000€' },
    { value: 'undefined', label: 'À définir' },
  ];

  const timelines = [
    { value: '1month', label: 'Dans le mois' },
    { value: '3months', label: 'Dans les 3 mois' },
    { value: '6months', label: 'Dans les 6 mois' },
    { value: '1year', label: "D'ici 1 an" },
    { value: 'exploring', label: 'Je me renseigne' },
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.company_name.trim()) newErrors.company_name = 'Nom de l\'entreprise requis';
      if (!formData.sector) newErrors.sector = 'Secteur requis';
      if (!formData.employee_count) newErrors.employee_count = 'Taille de l\'équipe requise';
    }

    if (step === 2) {
      if (!formData.first_name.trim()) newErrors.first_name = 'Prénom requis';
      if (!formData.last_name.trim()) newErrors.last_name = 'Nom requis';
      if (!formData.email.trim()) {
        newErrors.email = 'Email requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
    }

    if (step === 3) {
      if (!formData.main_challenge) newErrors.main_challenge = 'Défi principal requis';
      if (!formData.budget_range) newErrors.budget_range = 'Budget requis';
      if (!formData.timeline) newErrors.timeline = 'Délai requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    if (!formData.consent) {
      setErrors({ consent: 'Veuillez accepter les conditions' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get UTM params and quiz data
      const urlParams = new URLSearchParams(window.location.search);

      // Read score and level from localStorage (robust source)
      const storedScore = localStorage.getItem('lead_score');
      const storedLevel = localStorage.getItem('lead_level');
      const hasScore = storedScore !== null && storedScore !== '';
      
      const finalScore = hasScore ? parseInt(storedScore, 10) : null;
      const finalLevel = storedLevel || null;
      
      console.log('[Form] Submitting with score:', finalScore, 'level:', finalLevel, 'hasScore:', hasScore);

      const leadData = {
        ...formData,
        quiz_score: finalScore,
        quiz_level: finalLevel,
        quiz_id: sessionStorage.getItem('quiz_id') || null,
        source: hasScore ? 'Quiz' : 'Formulaire qualifiant',
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        page_url: window.location.href,
        consent: formData.consent,
      };

      await createLead(leadData);
      
      // Clear quiz data after submission
      sessionStorage.removeItem('quizData');
      localStorage.removeItem('lead_score');
      localStorage.removeItem('lead_level');
      
      // Track conversion
      trackGenerateLead(leadData);
      trackCTAClick('form_submit', 'qualifying_form');

      // Clear quiz data
      sessionStorage.removeItem('quiz_score');
      sessionStorage.removeItem('quiz_id');

      // Redirect to thank you page
      navigate('/merci');
    } catch (err) {
      console.error('Form submission error:', err);
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <div className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                currentStep > step.number
                  ? 'bg-green-600 text-white'
                  : currentStep === step.number
                  ? 'bg-[#1C2E4A] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
              data-testid={`form-step-indicator-${step.number}`}
            >
              {currentStep > step.number ? (
                <Check className="w-5 h-5" />
              ) : (
                step.number
              )}
            </div>
            <span className={`ml-2 text-sm font-medium hidden sm:block ${
              currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
            }`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-12 sm:w-24 h-0.5 mx-2 ${
              currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="form-step-1">
      <div>
        <Label htmlFor="company_name" className="text-gray-700 font-medium">
          Nom de l'entreprise *
        </Label>
        <Input
          id="company_name"
          value={formData.company_name}
          onChange={(e) => handleChange('company_name', e.target.value)}
          placeholder="Ex: Boulangerie Dupont"
          className={`mt-2 ${errors.company_name ? 'border-red-500' : ''}`}
          data-testid="form-company-name"
          aria-invalid={!!errors.company_name}
          aria-describedby={errors.company_name ? 'company_name-error' : undefined}
        />
        {errors.company_name && (
          <p id="company_name-error" className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.company_name}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="sector" className="text-gray-700 font-medium">
          Secteur d'activité *
        </Label>
        <Select value={formData.sector} onValueChange={(value) => handleChange('sector', value)}>
          <SelectTrigger
            id="sector"
            className={`mt-2 ${errors.sector ? 'border-red-500' : ''}`}
            data-testid="form-sector"
            aria-invalid={!!errors.sector}
          >
            <SelectValue placeholder="Sélectionnez votre secteur" />
          </SelectTrigger>
          <SelectContent>
            {sectors.map((sector) => (
              <SelectItem key={sector.value} value={sector.value}>
                {sector.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.sector && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.sector}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="employee_count" className="text-gray-700 font-medium">
          Taille de l'équipe *
        </Label>
        <Select value={formData.employee_count} onValueChange={(value) => handleChange('employee_count', value)}>
          <SelectTrigger
            id="employee_count"
            className={`mt-2 ${errors.employee_count ? 'border-red-500' : ''}`}
            data-testid="form-employee-count"
            aria-invalid={!!errors.employee_count}
          >
            <SelectValue placeholder="Sélectionnez la taille" />
          </SelectTrigger>
          <SelectContent>
            {employeeCounts.map((count) => (
              <SelectItem key={count.value} value={count.value}>
                {count.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.employee_count && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.employee_count}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="website" className="text-gray-700 font-medium">
          Site web
        </Label>
        <Input
          id="website"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
          placeholder="https://www.exemple.fr"
          className="mt-2"
          data-testid="form-website"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6" data-testid="form-step-2">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name" className="text-gray-700 font-medium">
            Prénom *
          </Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            placeholder="Jean"
            className={`mt-2 ${errors.first_name ? 'border-red-500' : ''}`}
            data-testid="form-first-name"
            aria-invalid={!!errors.first_name}
          />
          {errors.first_name && (
            <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
              {errors.first_name}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="last_name" className="text-gray-700 font-medium">
            Nom *
          </Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            placeholder="Dupont"
            className={`mt-2 ${errors.last_name ? 'border-red-500' : ''}`}
            data-testid="form-last-name"
            aria-invalid={!!errors.last_name}
          />
          {errors.last_name && (
            <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
              {errors.last_name}
            </p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="email" className="text-gray-700 font-medium">
          Email professionnel *
        </Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="jean.dupont@entreprise.fr"
          className={`mt-2 ${errors.email ? 'border-red-500' : ''}`}
          data-testid="form-email"
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.email}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          Nous ne partagerons jamais votre email.
        </p>
      </div>

      <div>
        <Label htmlFor="phone" className="text-gray-700 font-medium">
          Téléphone (optionnel)
        </Label>
        <Input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          placeholder="06 12 34 56 78"
          className="mt-2"
          data-testid="form-phone"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6" data-testid="form-step-3">
      <div>
        <Label htmlFor="main_challenge" className="text-gray-700 font-medium">
          Quel est votre principal défi ? *
        </Label>
        <Select value={formData.main_challenge} onValueChange={(value) => handleChange('main_challenge', value)}>
          <SelectTrigger
            id="main_challenge"
            className={`mt-2 ${errors.main_challenge ? 'border-red-500' : ''}`}
            data-testid="form-main-challenge"
            aria-invalid={!!errors.main_challenge}
          >
            <SelectValue placeholder="Sélectionnez votre défi principal" />
          </SelectTrigger>
          <SelectContent>
            {challenges.map((challenge) => (
              <SelectItem key={challenge.value} value={challenge.value}>
                {challenge.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.main_challenge && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.main_challenge}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="budget_range" className="text-gray-700 font-medium">
          Budget envisagé *
        </Label>
        <Select value={formData.budget_range} onValueChange={(value) => handleChange('budget_range', value)}>
          <SelectTrigger
            id="budget_range"
            className={`mt-2 ${errors.budget_range ? 'border-red-500' : ''}`}
            data-testid="form-budget-range"
            aria-invalid={!!errors.budget_range}
          >
            <SelectValue placeholder="Sélectionnez une fourchette" />
          </SelectTrigger>
          <SelectContent>
            {budgetRanges.map((range) => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.budget_range && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.budget_range}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="timeline" className="text-gray-700 font-medium">
          Quand souhaitez-vous démarrer ? *
        </Label>
        <Select value={formData.timeline} onValueChange={(value) => handleChange('timeline', value)}>
          <SelectTrigger
            id="timeline"
            className={`mt-2 ${errors.timeline ? 'border-red-500' : ''}`}
            data-testid="form-timeline"
            aria-invalid={!!errors.timeline}
          >
            <SelectValue placeholder="Sélectionnez un délai" />
          </SelectTrigger>
          <SelectContent>
            {timelines.map((tl) => (
              <SelectItem key={tl.value} value={tl.value}>
                {tl.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.timeline && (
          <p className="mt-1 text-sm text-red-600" role="alert" aria-live="polite">
            {errors.timeline}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="additional_info" className="text-gray-700 font-medium">
          Informations complémentaires (optionnel)
        </Label>
        <Textarea
          id="additional_info"
          value={formData.additional_info}
          onChange={(e) => handleChange('additional_info', e.target.value)}
          placeholder="Décrivez votre situation ou vos attentes..."
          className="mt-2 min-h-[100px]"
          data-testid="form-additional-info"
        />
      </div>

      {/* Needs checkboxes */}
      <div>
        <Label className="text-gray-700 font-medium mb-3 block">
          Domaines qui vous intéressent
        </Label>
        <div className="space-y-3">
          {needsOptions.map((need) => (
            <div key={need.value} className="flex items-center space-x-3">
              <Checkbox
                id={`need-${need.value}`}
                checked={formData.needs.includes(need.value)}
                onCheckedChange={(checked) => {
                  const newNeeds = checked
                    ? [...formData.needs, need.value]
                    : formData.needs.filter(n => n !== need.value);
                  handleChange('needs', newNeeds);
                }}
                data-testid={`form-need-${need.value}`}
              />
              <Label htmlFor={`need-${need.value}`} className="font-normal cursor-pointer">
                {need.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* RGPD Consent */}
      <div className="flex items-start space-x-3 pt-4 border-t border-gray-200">
        <Checkbox
          id="consent"
          checked={formData.consent}
          onCheckedChange={(checked) => handleChange('consent', checked)}
          className={errors.consent ? 'border-red-500' : ''}
          data-testid="form-consent"
        />
        <div>
          <Label htmlFor="consent" className="font-normal cursor-pointer text-gray-700">
            J'accepte d'être recontacté dans le cadre de ma demande. *
          </Label>
          {errors.consent && (
            <p className="mt-1 text-sm text-red-600">{errors.consent}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section
      id="formulaire"
      className="py-16 md:py-24"
      style={{ backgroundColor: '#E9DFCF' }}
      data-testid="qualifying-form-section"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Quiz Score Summary - Uses localStorage data */}
        {scoreData.score !== null && (
          <div
            className="mb-6 p-4 rounded-xl text-center"
            style={{ backgroundColor: '#1C2E4A' }}
            data-testid="form-quiz-score-summary"
          >
            <p className="text-white font-semibold text-lg">
              Votre score : {scoreData.score}/100 • Niveau : {scoreData.level}
            </p>
          </div>
        )}

        {/* Hidden inputs for score and level */}
        <input type="hidden" name="score" value={scoreData.score || ''} />
        <input type="hidden" name="level" value={scoreData.level || ''} />

        {/* Header */}
        <div className="text-center mb-8">
          <h2
            className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 mb-4"
            style={{ letterSpacing: '-0.04em' }}
          >
            Demandez votre accompagnement
          </h2>
          <p className="text-lg text-gray-600">
            Remplissez ce formulaire et nous vous recontacterons sous 24h.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200">
          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Form Steps */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Global error */}
          {errors.submit && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg" role="alert" aria-live="polite">
              {errors.submit}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between gap-4 mt-8 pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="rounded-full"
              data-testid="form-prev-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Précédent
            </Button>

            {currentStep < 3 ? (
              <Button
                onClick={handleNext}
                className="bg-[#1C2E4A] hover:bg-[#0F1E33] text-white rounded-full"
                data-testid="form-next-btn"
              >
                Suivant
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#1C2E4A] hover:bg-[#0F1E33] text-white rounded-full"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <Check className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* RGPD notice */}
          <p className="mt-6 text-xs text-gray-500 text-center">
            En soumettant ce formulaire, vous acceptez que vos données soient traitées conformément à notre{' '}
            <a href="#confidentialite" className="hover:underline" style={{ color: '#1C2E4A' }}>
              politique de confidentialité
            </a>
            . Vos informations sont utilisées uniquement pour répondre à votre demande.
          </p>
        </div>
      </div>
    </section>
  );
};

export default QualifyingForm;
